<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget } from '@/interfaces/Dashboard';
import debounce from 'lodash/debounce';
import ConnxSkeletonExpiredLicencesCount from './ConnxSkeletonExpiredLicencesCount.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxStatusCard from '@/components/Atoms/ConnxStatusCard.vue';
import type { Status } from '@/interfaces/Dashboard';
import ConnxStatusRowCard from '@/components/Atoms/ConnxStatusRowCard.vue';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();

const total = ref<number>(0);
const nonCompliantPercent = ref<number>(0);
const nonCompliantCount = ref<number>(0);
const series = ref<any[] | null>(null);
const loading = ref(true);

const dashboardStore = useDashboardStore();

const status = computed<Status>(() => {
  return 'danger';
});
const seriesToShow = computed(() => {
  return (series.value && series.value.filter((s: any) => s.name !== '' && s.data !== 0)) ?? [];
});
const badgeText = computed(() => {
  return 'Non-Compliant';
});

const labeltext = ref();

const showList = computed(() => {
  if (!props.widget) return false;
  if (props.widget.defaultJsonProperties.h <= 3 && props.widget.defaultJsonProperties.w <= 4) {
    return false;
  }
  return seriesToShow.value && seriesToShow.value.length > 0;
});

const listOnRight = computed(() => {
  if (!props.widget) return false;
  return props.widget.defaultJsonProperties.w > 4;
});

const fetchData = async () => {
  try {
    const data = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    if (data?.data) {
      total.value = data.data.total ?? 0;
      nonCompliantPercent.value = data.data.nonCompliantPercent ?? 0;
      nonCompliantCount.value = data.data.nonCompliantCount ?? 0;
      series.value = data.data.series ?? [];

      labeltext.value = `${nonCompliantCount.value} of ${total.value} employees`;
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  } finally {
    loading.value = false;
  }
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;

    return {
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
    };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);

onMounted(fetchData);
</script>

<template>
  <ConnxSkeletonExpiredLicencesCount v-if="loading"></ConnxSkeletonExpiredLicencesCount>
  <div
    v-else-if="series && series.length > 0"
    class="tw-w-full tw-h-full tw-p-0 ScrollBar ScrollBar--vertical"
    :class="listOnRight ? 'tw-flex tw-flex-row tw-gap-6' : 'tw-flex tw-flex-col'"
    :style="{ height: `${height}px`, overflowY: 'auto' }"
  >
    <div class="tw-w-full tw-pr-4">
      <!-- Big Card -->
      <ConnxStatusCard
        :title="`${nonCompliantPercent}%`"
        :status="status"
        :badge-text="badgeText"
        :label-text="labeltext"
      />

      <!-- Subtitle -->
      <div
        v-if="showList"
        class="tw-flex-1"
        :class="{
          'tw-flex tw-flex-col tw-items-stretch tw-justify-center tw-grow': listOnRight,
        }"
      >
        <h4 class="tw-text-base tw-mb-4 tw-text-connx-grey-shade-55 tw-font-semibold tw-mt-4">
          Number of non-compliant employees in position
        </h4>
        <div class="tw-flex tw-flex-col tw-gap-2">
          <ConnxStatusRowCard
            :status="status"
            v-for="(item, index) in seriesToShow"
            :key="index"
            :title="item.name"
            :value="item.data"
          ></ConnxStatusRowCard>
        </div>
      </div>
    </div>
  </div>

  <ConnxDashboardWidgetNoData v-else />
</template>
